// Tab icon, three states:
//   offline  - greyed-out printer with a red badge (no serial port open)
//   ready    - printer with a steady green LED (connected, idle)
//   printing - the sheet scrolls and the LED blinks while data is arriving
// Printing wins over the resting state and falls back to it ~1 s after the last byte.
//
// The sheet coming out of the printer is blank until something is printed on it.
// It stays blank when offline and when freshly connected; once the C64 prints it
// shows text (frozen when printing stops) until the next page break, then it is a
// fresh blank sheet again (see pageBreak()).

const SIZE = 64;
const ACTIVE_MS = 1200; // keep animating this long after the last byte
const FRAME_MS = 140;

const PALETTE = {
  printing: { body: '#5b6b82', slot: '#3f4b5e', ink: '#2b2f36', paper: '#ffffff' },
  ready: { body: '#5b6b82', slot: '#3f4b5e', ink: '#2b2f36', paper: '#ffffff' },
  offline: { body: '#a3a9b3', slot: '#8a909b', ink: '#7d838d', paper: '#eceef1' },
};

// mode: 'offline' | 'ready' | 'printing'
function draw(ctx, now, mode, sheetText) {
  const printing = mode === 'printing';
  const t = printing ? now : 0; // resting icons are identical every time, only printing animates
  const c = PALETTE[mode];
  ctx.clearRect(0, 0, SIZE, SIZE);

  // sheet coming out of the front (scrolling text lines, clipped to the sheet)
  ctx.save();
  ctx.beginPath();
  ctx.rect(16, 38, 32, 26);
  ctx.clip();
  ctx.fillStyle = c.paper;
  ctx.fillRect(16, 38, 32, 26);
  ctx.fillStyle = c.ink;
  const offset = printing ? (t / FRAME_MS) % 8 : 0;
  const widths = [24, 18, 22, 12, 20, 16, 24, 10];
  if (sheetText) {
    for (let i = -1; i < 5; i++) {
      const y = 42 + i * 8 + offset;
      ctx.fillRect(20, y, widths[(i + 8 + Math.floor(t / (FRAME_MS * 8))) % 8], 3);
    }
  }
  ctx.restore();
  ctx.strokeStyle = c.ink;
  ctx.lineWidth = 2;
  ctx.strokeRect(16, 38, 32, 26);

  // paper feeding in at the top
  ctx.fillStyle = c.paper;
  ctx.fillRect(18, 4, 28, 14);
  ctx.strokeRect(18, 4, 28, 14);

  // body
  ctx.fillStyle = c.body;
  ctx.beginPath();
  ctx.roundRect(4, 16, 56, 26, 5);
  ctx.fill();
  ctx.fillStyle = c.slot;
  ctx.fillRect(10, 36, 44, 4); // output slot

  // LED: dark when offline, steady green when ready, blinking while printing
  const blinkOn = Math.floor(t / FRAME_MS) % 2 === 0;
  ctx.fillStyle = mode === 'offline' ? '#7d838d' : printing ? (blinkOn ? '#33d17a' : '#1d6b40') : '#33d17a';
  ctx.beginPath();
  ctx.arc(51, 24, 3, 0, Math.PI * 2);
  ctx.fill();

  if (mode === 'offline') {
    // red badge with a white cross, bottom right
    ctx.fillStyle = '#e5484d';
    ctx.beginPath();
    ctx.arc(50, 50, 12, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3.5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(45, 45); ctx.lineTo(55, 55);
    ctx.moveTo(55, 45); ctx.lineTo(45, 55);
    ctx.stroke();
  }
}

// link: the <link rel="icon"> element to update. logo (optional): an <img> that
// shows the very same icon, e.g. in the page header.
export function createFavicon(link, logo = null) {
  const canvas = Object.assign(document.createElement('canvas'), { width: SIZE, height: SIZE });
  const ctx = canvas.getContext('2d');
  let activeUntil = -Infinity;
  let connected = false;
  let hasPrint = false; // something was printed on the current sheet
  let timer = null;

  const resting = () => (connected ? 'ready' : 'offline');
  const show = (mode) => {
    // Text on the sheet: while printing, and afterwards until the next page break
    // (never when offline, where the printer is greyed out and empty).
    const sheetText = mode === 'printing' || (mode === 'ready' && hasPrint);
    draw(ctx, performance.now(), mode, sheetText);
    const url = canvas.toDataURL('image/png');
    link.type = 'image/png';
    link.href = url;
    if (logo) logo.src = url;
  };

  const tick = () => {
    const printing = performance.now() < activeUntil;
    show(printing ? 'printing' : resting());
    if (!printing) {
      clearInterval(timer);
      timer = null;
    }
  };

  show('offline');
  return {
    // Call whenever printer data arrives. `ms`: how long to keep animating (a print server delivers a whole
    // flush at once, so it asks for longer than the byte-by-byte USB stream).
    pulse(ms = ACTIVE_MS) {
      hasPrint = true;
      activeUntil = Math.max(activeUntil, performance.now() + ms);
      if (!timer) {
        tick();
        timer = setInterval(tick, FRAME_MS);
      }
    },
    // Call when the serial port is opened (true) or closed (false).
    setConnected(value) {
      connected = value;
      hasPrint = false; // fresh connection (or none): blank sheet
      if (!timer) show(resting());
    },
    // Call on any page break (hardware button, on-screen button, page filled up)
    // and when everything is cleared: the sheet is blank until printed on again.
    pageBreak() {
      hasPrint = false;
      if (!timer) show(resting());
    },
  };
}
